/**
 * CartPage duck.
 *
 * The shopping cart is stored client-side in localStorage (see src/ducks/cart.duck.js), so the Cart
 * page fetches the referenced listings on the client rather than through the SSR loadData pattern.
 * This duck keeps track of the fetch status and stores the fetched listing/author entities in the
 * shared marketplaceData entities store.
 */
import { createSlice } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ================ Slice ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  listingIds: [],
};

const cartPageSlice = createSlice({
  name: 'CartPage',
  initialState,
  reducers: {
    fetchCartListingsRequest: state => {
      state.fetchInProgress = true;
      state.fetchError = null;
    },
    fetchCartListingsSuccess: (state, action) => {
      state.fetchInProgress = false;
      state.listingIds = action.payload;
    },
    fetchCartListingsError: (state, action) => {
      state.fetchInProgress = false;
      state.fetchError = action.payload;
    },
  },
});

export const {
  fetchCartListingsRequest,
  fetchCartListingsSuccess,
  fetchCartListingsError,
} = cartPageSlice.actions;

export default cartPageSlice.reducer;

// ================ Thunks ================ //

/**
 * Fetch the listings referenced by the cart (including author, images and current stock).
 *
 * @param {string[]} listingIds - The listing ids currently in the cart.
 * @param {Object} config - The app configuration (for image variants).
 */
export const fetchCartListings = (listingIds, config) => (dispatch, getState, sdk) => {
  dispatch(fetchCartListingsRequest());

  if (!Array.isArray(listingIds) || listingIds.length === 0) {
    dispatch(fetchCartListingsSuccess([]));
    return Promise.resolve([]);
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  // Fetch each listing individually so a single missing/closed listing doesn't fail the rest.
  return Promise.all(
    listingIds.map(id =>
      sdk.listings
        .show({ id, ...params })
        .then(response => {
          dispatch(addMarketplaceEntities(response));
          return id;
        })
        .catch(() => null)
    )
  )
    .then(resolvedIds => {
      const availableIds = resolvedIds.filter(Boolean);
      dispatch(fetchCartListingsSuccess(availableIds));
      return availableIds;
    })
    .catch(e => {
      dispatch(fetchCartListingsError(storableError(e)));
      throw e;
    });
};
